<template>
<div v-if="Object.hasOwn(formValues, 'isEnabled')" class="p-3 pt-0">
<h1 class="px-3">
	<span v-if="isCreateUser" class="fs-5">Створення нового користувача</span>
	<span v-if="isUpdateUser" class="fs-5">{{account?.name ?? '-'}} ({{ account?.login ?? '-' }})</span>
</h1>

<Form
	@submit="onSubmit"
	@invalid-submit="console.log"
	:validation-schema="schema"
	:initial-values="formValues"
	v-slot="{ errors, meta: meta, values, setFieldValue, setFieldTouched }"
>
	<div class="row form-row my-3">
		<div class="form-group col">
			<label>{{ capitalizeFirstLetter(accountSchema.login.title) }}&nbsp;*</label>
			<Field
				:name="this.accountStore.schema.login"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.login] }"
				:disabled="!isCreateUser"
			/>
			<span class="form-text fst-italic">
				Латинські літери, від 5 до 20 символів.
			</span>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.login]}}</div>
		</div>
	</div>

	<div class="row form-row my-3">
		<div class="form-group col-6">
			<label>{{ capitalizeFirstLetter(accountSchema.password.title) }}&nbsp;*</label>
			<Field
				:name="this.accountStore.schema.password"
				type="password"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.password] }"
				:disabled="!accountStore.permissions.includes('manage_users')"
			/>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.password]}}</div>
		</div>
		<div class="form-group col-6">
			<label>Підтвердіть {{accountSchema.password.title}}&nbsp;*</label>
			<Field
				name="confirmPassword"
				type="password"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors.confirmPassword }"
				:disabled="!accountStore.permissions.includes('manage_users')"
			/>
			<div class="invalid-feedback">{{errors.confirmPassword}}</div>
		</div>
		<div class="form-group col-12">
			<span class="form-text fst-italic">
				Латинські літери, знаки, від 5 до 20 символів.
			</span>
			<span
				v-if="isUpdateUser"
				class="form-text text-danger fst-italic"
			>
				Пароль Gotify також буде змінено.
			</span>
		</div>
	</div>

	<div class="row form-row my-3">
		<div class="form-group col">
			<label>{{ capitalizeFirstLetter(accountSchema.name.title) }} &nbsp;*</label>
			<Field
				:name="this.accountStore.schema.name"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.name] }"
			/>
			<span class="form-text fst-italic">
				Текст з пробілами, від 5 до 30 символів.
			</span>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.name]}}</div>
		</div>
	</div>

	<div class="row form-row my-3">
		<div class="form-group col">
			<label>{{ capitalizeFirstLetter(accountSchema.phone.title) }}</label>
			<Field
				:name="this.accountStore.schema.phone"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.phone] }"
			/>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.phone]}}</div>
		</div>
	</div>

	<div class="row form-row my-3">
		<div class="form-group col">
			<label>{{ capitalizeFirstLetter(accountSchema.position.title) }}</label>
			<Field
				:name="this.accountStore.schema.position"
				type="text"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.position] }"
			/>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.position]}}</div>
		</div>
	</div>
	
	<div class="row form-row my-3">
		<div class="form-group col-12">
			<label>{{ capitalizeFirstLetter(accountSchema.role.title) }}&nbsp;*</label>
			<Field
				:name="this.accountStore.schema.role"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.role] }"
				:disabled="!isCreateUser"
				:enabled = "true"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option
					v-for="role in Object.values(accountSchema.role.allowed)"
					:key="role.key"
					:value="role.key"
				>
					{{ capitalizeFirstLetter(role.title) }}
				</option>
			</Field>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.role]}}</div>
		</div>
		<div class="form-group col-12">
			<span class="form-text fst-italic">
				Визначає рівень доступа.<br>
				<ul>
					<li><b>{{ capitalizeFirstLetter(accountSchema.role.allowed.visitor.title) }}</b> може переглядати все, що належить організації.</li>
					<li><b>{{ capitalizeFirstLetter(accountSchema.role.allowed.dispatcher.title) }}</b> як відвідувач плюс може керувати станціями в організації.</li>
					<li><b>{{ capitalizeFirstLetter(accountSchema.role.allowed.manager.title) }}</b> як диспетчер плюс може створювати/видаляти СКЗ, КВП в організації.</li>
					<li><b>{{ capitalizeFirstLetter(accountSchema.role.allowed.engineer.title) }}</b> як менеджер плюс може калібрувати станції.</li>
					<li><b>{{ capitalizeFirstLetter(accountSchema.role.allowed.admin.title) }}</b> як менеджер плюс може створювати/видаляти користувачів в організації.</li>
				</ul>
			</span>
		</div>
	</div>

	<div class="row form-row my-3">
		<div class="form-group col-12">
			<label>{{ capitalizeFirstLetter(accountSchema.organization.title) }}&nbsp;*</label>
			<Field
				:name="this.accountStore.schema.organizationId"
				as="select"
				class="form-control form-control-sm"
				:class="{ 'is-invalid': errors[this.accountStore.schema.organizationId] }"
				:disabled="!isCreateUser || !accountStore.isPrimary"
			>
				<option value="" disabled>Натисніть щоб обрати</option>
				<option v-for="org in organizations" :key="org._id" :value="org._id">{{ org.title }}</option>
			</Field>
			<div class="invalid-feedback">{{errors[this.accountStore.schema.organizationId]}}</div>
		</div>
	</div>

	<!-- Accordion with sector forms for every department in organization -->
	<div class="row form-row accordion accordion-flush mt-2 mb-3" id="departments-accordion">
		<div class="form-group col-auto input-min-width mt-1 me-5">
			<label class="mb-2">Адміністративні ланки в полі зору</label>
			<aside
				v-if="organization(values.organization_id)?.departments.length == 0"
				class="text-body-secondary fst-italic ms-4"
			>
				Не створено ланок 2-го рівня
			</aside>
			<div
				v-for="dep in organization(values.organization_id)?.departments"
				:key="dep.id"
				class="accordion-item me-0 my-0"
			>
				<div class="accordion-header row pt-0">
					<div class="col-auto">
						<input
							class="form-check-input"
							type="checkbox"
							value="true"
							:id="dep.id"
							@change="(event) => {
								onCheckDepartment(dep.id, event.target.checked, setFieldValue, values.departments_scope);
								dep.sectors.forEach((sector) => {
									onCheckSector(sector.id, event.target.checked, setFieldValue, values.sectors_scope);
								})
								setFieldTouched('departments_scope', true);
							}"
							:checked="values.departments_scope.includes(dep.id)"
							:disabled="!accountStore.permissions.includes('manage_users')"
						>
						<label class="form-check-label ms-2" :for="dep.id">
							{{ dep.title }}
						</label>
					</div>

					<div class="col">
						<button
							class="accordion-button collapsed p-1"
							style="line-height: 1.5; font-size: 0.875rem; "
							type="button"
							data-bs-toggle="collapse"
							:data-bs-target="`#department-${dep.id}-sectors`"
							aria-expanded="false"
							:aria-controls="`department-${dep.id}-sectors`"
						>
							&nbsp;
						</button>
					</div>
				</div>
				<div
					:id="`department-${dep.id}-sectors`"
					class="accordion-collapse collapse"
					data-bs-parent="#departments-accordion"
				>
					<div class="accordion-body p-0 ps-4 pb-4">
						<aside v-if="dep.sectors.length == 0" class="text-body-secondary fst-italic">
							Не створено ланок 3-го рівня
						</aside>
						<div
							class="form-check py-1"
							v-for="sector in dep.sectors"
							:key="sector.id"
						>
							<input
								class="form-check-input"
								type="checkbox"
								value="true"
								:id="sector.id"
								@change="(event) => {
									onCheckSector(sector.id, event.target.checked, setFieldValue, values.sectors_scope);
									if (event.target.checked) {
										onCheckDepartment(dep.id, true, setFieldValue, values.departments_scope)
									} else if (dep.sectors.every((sector) => !values.sectors_scope.includes(sector.id))) {
										onCheckDepartment(dep.id, false, setFieldValue, values.departments_scope)
									}
									setFieldTouched('sectors_scope', true);
								}"
								:checked="values.sectors_scope.includes(sector.id)"
								:disabled="!accountStore.permissions.includes('manage_users')"
							>
							<label class="form-check-label" :for="sector.id">
								{{ sector.title }}
							</label>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<div class="form-row my-3">
		<Field
			:name="this.accountStore.schema.isEnabled"
			type="checkbox"
			id="is-enabled"
			class="form-check-input"
			:class="{ 'is-invalid': errors[this.accountStore.schema.isEnabled] }"
			:value="true"
			:uncheckedValue="false"
			:disabled="isSelfUpdate"
		/>
		<label for="is-enabled" class="form-check-label mx-2">
			{{ capitalizeFirstLetter(accountSchema.isEnabled.title) }}?
		</label>
		<div class="invalid-feedback">{{errors[this.accountStore.schema.isEnabled]}}</div>
	</div>

	<div class="row form-row my-3">
		<span class="form-text text-decoration-underline">
			*&nbsp;поля обов'язкові для заповнення
		</span>
	</div>

	<div class="row form-row my-3">
		<span class="form-text fst-italic">
			<p 
				v-if="executionResultMessage.length > 0"
				class="fs-5"
				:class="{'text-success': executionOk, 'text-danger': !executionOk}"
			>
				{{ executionResultMessage }}
			</p>
		</span>
	</div>
	<div class="row form-row mt-5">
		<div class="form-group col-auto ms-0 me-auto">
			&nbsp;
			<button
				v-if="isUpdateUser"
				type="button"
				class="btn btn-danger btn-sm"
				@click="deleteConfirmationPopup=true"
				:disabled="isSelfUpdate"
			>
				Видалити
			</button>
			<confirmation-popup
				v-model:show="deleteConfirmationPopup"
				@confirm="deleteUser"
			>
				<template v-slot:body>
					<p>
						Ви впевнені що хочете <b>видалити</b> користувача?
					</p>
				</template>
			</confirmation-popup>
		</div>

		<div class="form-group col-auto">&nbsp;</div>

		<!-- <div class="form-group col-auto ms-auto me-2">
			<button
				type="create"
				class="btn btn-secondary btn-sm mx-2"
				@click="onCancel"
			>
				Відмінити
			</button>
		</div> -->
		<div class="form-group col-auto me-0">
			<button
				type="submit"
				class="btn btn-primary btn-sm"
				:disabled="!meta.valid || !meta.touched"
			>
				{{ isCreateUser ? 'Створити' : '' }}
				{{ isUpdateUser ? 'Зберегти' : '' }}
			</button>
			<confirmation-popup
				v-model:show="updateConfirmationPopup"
				@confirm="isCreateUser ? create() : update()"
			>
				<template v-slot:body>
					<p>
						Ви впевнені що хочете <b>{{ isCreateUser ? 'створити' : 'відредагувати'}}</b> користувача?
					</p>
				</template>
			</confirmation-popup>
		</div>
	</div>
	<!-- {{ errors }}<br>
	{{ meta }} -->
	<!-- departments: {{ values.departments_scope }} <br>
	sectors: {{ values.sectors_scope }} -->
</Form>
</div>
</template>


<script>
import {
	RESOURCE_NAMES,
	readResources, 
	// updateResourceItem, deleteResourceItem,
	createUser, updateUser, createApplicationForUser, setApplicationIcon, deleteUser,
	LOW_PRIORITY_MESSAGES_1, NORMAL_PRIORITY_MESSAGES_1, 
} from '@/api/api.js'
import { capitalizeFirstLetter, dateTimeToString } from '@/helpers.js'
import ConfirmationPopup from '../ConfirmationPopup.vue'
import { GOTIFY_ICONS_URL } from '@/app.conf'
// import { SHA256 } from 'crypto-js'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useSessionStore } from '@/stores/SessionStore.js'
import { useAccounts } from '@/composables/accounts.js'
// Validation
// https://jasonwatmore.com/post/2020/10/01/vue-3-veevalidate-form-validation-example
import { Form, Field } from 'vee-validate'
// https://github.com/jquense/yup/tree/a58f02d2f6164c46a9757a818eebac582f7a441c#boolean
import * as Yup from 'yup'


export default {
	waitAfterExecuteSuccessMs: 3100,
	requiredPromt: 'Обов\'язкове поле',
	overMinLen: 'Довжина меньше мінімальної',
	overMaxLen: 'Довжина більше максимальної',

	components: {
        Form,
        Field,
		ConfirmationPopup,
    },

	props: {
	},

	emits: {
		updated: null,
		close: null,
	},

	setup() {
		//console.log("Setup create update user popup.")
		const accountStore = useAccontStore()
		const sessionStore = useSessionStore()
		const {
			accountSchema,
			allAccounts,
			getAccount,
			fetchAllLogins,
			invalidateAccounts,
		} = useAccounts()

		return {
			capitalizeFirstLetter,
			dateTimeToString,
			accountStore,
			sessionStore,

			accountSchema,
			allAccounts,
			getAccount,
			fetchAllLogins,
			invalidateAccounts,
		}
	},

	data() {
		// Schema for validation.
		// !!! Fields must bee same as accountStore.schema !!!
		const schema = Yup.object().shape({
            login: Yup.string()
                .required(this.$options.requiredPromt)
				.min(5, this.$options.overMinLen)
				.max(20, this.$options.overMaxLen)
				.matches(
					/^(?![_.])(?!.*[_.]{2})[a-zA-Z0-9._]+(?<![_.])$/,
					'Може містити тільки латинські букви і цифри'
				),
			password: Yup.string()
				.min(5, this.$options.overMinLen)
				.max(20, this.$options.overMaxLen)
                .required(this.$options.requiredPromt),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref('password'), null], 'Паролі мають співпадати')
                .required(this.$options.requiredPromt),
            name: Yup.string()
				.min(5, this.$options.overMinLen)
				.max(30, this.$options.overMaxLen)
                .required(this.$options.requiredPromt)
				.matches(/^[a-zA-Z0-9а-яА-ЯґҐєЄіІїЇўЎёЁ() -]*$/, 'Може містити тільки букви і цифри'),
			phone: Yup.string()
				.max(13, this.$options.overMaxLen)
				.matches(/^[0-9+]*$/, 'Може містити тільки цифри і знак +'),
			position: Yup.string()
				.max(64, this.$options.overMaxLen)
				.matches(/^[a-zA-Z0-9а-яА-ЯґҐєЄіІїЇўЎёЁ() -]*$/, 'Може містити тільки букви і цифри'),
			role: Yup.string()
				.required(this.$options.requiredPromt),
			organization_id: Yup.string()
				.required(this.$options.requiredPromt),
			departments_scope: Yup.array().of(
				Yup.string()
					.min(36, this.$options.overMinLen)
					.max(36, this.$options.overMaxLen)
			),
			sectors_scope: Yup.array().of(
				Yup.string()
					.min(36, this.$options.overMinLen)
					.max(36, this.$options.overMaxLen)
			),
			isEnabled: Yup.string()
                .required(this.$options.requiredPromt),
        });

		return {
			// Copy of account
			account: null,
			//------------------ Form
			schema,
			formValues: {},
			organizations: [],
			//------------------
			userFingerprints: [],
			//
			updateConfirmationPopup: false,
			deleteConfirmationPopup: false,
			//
			executionResultMessage: '',
			executionOk: null,
		}
	},

	watch: {
		allAccounts: {
			immediate: true,
			async handler() {
				if (this.isCreateUser) {
					// console.log('create user')
					this.formValues = {
						isEnabled: true,
						organization_id: this.accountStore.organization._id,
						departments_scope: [],
						sectors_scope: [],
					}
				} else if (this.isUpdateUser) {
					// Read account
					this.account = this.getAccount(this.userId)
					if (!this.account)
						return

					for (const [key] of Object.entries(this.schema.fields)) {
						this.formValues[key] = this.account[key]
					}
					
					this.formValues[this.accountSchema.id.key] = this.account[this.accountSchema.id.key]
					this.formValues[this.accountSchema.etag.key] = this.account[this.accountSchema.etag.key]
					this.formValues.password = 'password'
					this.formValues.confirmPassword = 'password'
					if (!this.formValues.departments_scope) {
						this.formValues.departments_scope = []
					}
					if (!this.formValues.sectors_scope) {
						this.formValues.sectors_scope = []
					}
				}
			},
		},
	},

	async created() {
		// load organizations list
		this.organizations = await readResources(
			RESOURCE_NAMES.organizations,
			this.accountStore.userLogin,
			this.accountStore.userPassword,
		)
		this.organizations = this.organizations.filter((org) => org.title)

		// console.log("formValues ", this.formValues)
		// console.log("organizations = ", this.organizations)
	},

	async mounted() {
		this.userFingerprints = await this.sessionStore.getUserSessions(this.userId)
	},

	methods: {
		async onSubmit(values, actions) {
            // display form values on success
			//alert('SUCCESS!! :-)\n\n' + JSON.stringify(values, null, 4));

			// Update form values
			this.formValues = values

			if (this.isCreateUser) {
				// Read all allready used logins
				const logins = await this.fetchAllLogins()

				if (logins == null) {
					this.executionResultMessage = 
						`Користувача не створено: помилка перевірки логіну.`
					this.executionOk = false
					return
				// Check if login is allready used
				} else if (logins.includes(this.formValues.login)) {
					actions.setFieldError(
						'login',
						`Цей ${this.accountSchema.login.title} вже використовується. Спробуйте інший.`
					)
					return
				}

				// Show confirmation popup
				this.updateConfirmationPopup = true
			} else if (this.isUpdateUser) {
				// Show confirmation popup
				this.updateConfirmationPopup = true
			}
        },

		// Create user and close popup
		async create() {
			let payload = { ...this.formValues }
			payload[this.accountStore.schema.gotify.self] = new Object()
			this.executionResultMessage = ''
			this.executionOk = false
			
			if (!await this._createGotify(payload)) {
				return
			}

			// Create user in aplication DB
			//let payload = { ... this.formValues }
			delete payload.confirmPassword
			// payload[this.accountStore.schema.password] = 
			// 	await SHA256(this.formValues[this.accountStore.schema.password]).toString()

			
			// Request server to create account
			this.accountStore.createAccount(payload)
			.then((/*result*/) => {
				//console.log("create account result = ", result)
				this.invalidateAccounts()
				this.executionResultMessage = `Користувача успішно створено.`
				this.executionOk = true
				
				this.goBackAfterTimeout()
			}).catch(err => {
				console.error("on create account", err)
				this.executionResultMessage = 
					`Користувача не створено: сервер повернув помилку.`
				this.executionOk = false
			})
			if (!this.executionOk) {
				this.deleteUser()
				return false
			}
		},

		async _createGotify(payload) {
			// Create user in Gotify service
			await createUser(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password]
			).then((response) => {
				this.executionResultMessage = `Користувача для мобільного додатку створено.`
				this.executionOk = true
				payload[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.userId] = response.id
			})
			.catch((err) => {
				console.error("on create Gotify user, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Користувача для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				return false
			}

			// Create apps for gotify user
			//
			let gotifyApp = {}
			let icon_url = ''
			let icon = null
			// Test messages app
			await createApplicationForUser(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				'Тестові повідомлення',
				LOW_PRIORITY_MESSAGES_1,
				'Тестування служби сповіщень через веб-інтерфейс',
			).then((response) => {
				this.executionResultMessage = `Тестове джерело сповіщень для мобільного додатку створено.`
				this.executionOk = true
				gotifyApp.id = response.id
				gotifyApp.token = response.token
			})
			.catch((err) => {
				console.error("on create Gotify app for user, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Тестове джерело сповіщень для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}
			payload[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.testToken] = gotifyApp.token

			icon_url = `${GOTIFY_ICONS_URL}/test_message.jpg`
			icon = await fetch(icon_url).then(r => r.blob())
			// Add app icon
			await setApplicationIcon(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				gotifyApp.id,
				icon
			).then((response) => {
				this.executionOk = response.ok
				if (response.ok) {
					this.executionResultMessage = `Аватар тестового джерела сповіщень для мобільного додатку створено.`
				} else {
					throw new Error(response)
				}
			})
			.catch((err) => {
				console.error("on create Gotify app icon for app, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Аватар тестового джерела сповіщень для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}

			// Station messages app
			await createApplicationForUser(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				'Повідомлення від СКЗ',
				NORMAL_PRIORITY_MESSAGES_1,
				'Повідомлення від всіх СКЗ в оганізації',
			).then((response) => {
				this.executionResultMessage = `Джерело сповіщень для станцій в для мобільного додатку створено.`
				this.executionOk = true
				gotifyApp.id = response.id
				gotifyApp.token = response.token
			})
			.catch((err) => {
				console.error("on create Gotify app for user, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Джерело сповіщень для станцій в для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}
			payload[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.stationsToken] = gotifyApp.token

			icon_url = `${GOTIFY_ICONS_URL}/station_icon.png`
			icon = await fetch(icon_url).then(r => r.blob())
			// Add app icon
			await setApplicationIcon(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				gotifyApp.id,
				icon
			).then((response) => {
				this.executionOk = response.ok
				if (response.ok) {
					this.executionResultMessage = `Аватар джерела сповіщень для станцій для мобільного додатку створено.`
				} else {
					throw new Error(response)
				}
			})
			.catch((err) => {
				console.error("on create Gotify app icon for app, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Аватар джерела сповіщень для станцій для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}

			// Post messages app
			await createApplicationForUser(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				'Повідомлення від КВП',
				NORMAL_PRIORITY_MESSAGES_1,
				'Повідомлення від всіх КВП в оганізації',
			).then((response) => {
				this.executionResultMessage = `Джерело сповіщень для КВП для мобільного додатку створено.`
				this.executionOk = true
				gotifyApp.id = response.id
				gotifyApp.token = response.token
			})
			.catch((err) => {
				console.error("on create Gotify app for user, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Джерело сповіщень для КВП для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}
			payload[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.postsToken] = gotifyApp.token

			icon_url = `${GOTIFY_ICONS_URL}/post_icon.png`
			icon = await fetch(icon_url).then(r => r.blob())
			// Add app icon
			await setApplicationIcon(
				this.formValues[this.accountStore.schema.login],
				this.formValues[this.accountStore.schema.password],
				gotifyApp.id,
				icon
			).then((response) => {
				this.executionOk = response.ok
				if (response.ok) {
					this.executionResultMessage = `Аватар джерела сповіщень для КВП для мобільного додатку створено.`
				} else {
					throw new Error(response)
				}
			})
			.catch((err) => {
				console.error("on create Gotify app icon for app, response:", err.response ? err.response.data : err)
				this.executionResultMessage = 
					`Аватар джерела сповіщень для КВП для мобільного додатку не створено: сервер повернув помилку.`
				this.executionOk = false
			});
			if (!this.executionOk) {
				this.deleteUser(true, false)
				return false
			}

			return true
		},

		// Update user and close popup (if closing requested)
		async update() {
			this.executionResultMessage = ''
			let result = false
			let payload = structuredClone(this.formValues)
			
			if (payload.password === 'password') {
				delete payload.password
			} else {
				// Update user password in Gotify service
				await updateUser(
					this.account[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.userId],
					this.formValues[this.accountStore.schema.login],
					payload.password
				).then(() => {
					this.executionResultMessage = `Пароль користувача для мобільного додатку оновлено.`
					this.executionOk = true
				})
				.catch((err) => {
					console.error("on update Gotify user, response:", err.response ? err.response.data : err)
					this.executionResultMessage = 
						`Пароль користувача для мобільного додатку не оновлено: сервер повернув помилку.`
					this.executionOk = false
				});
				if (!this.executionOk) {
					return
				}
				
				// Make password hash
				// payload[this.accountStore.schema.password] = 
				// 	await SHA256(payload.password).toString()
			}
			delete payload.confirmPassword

			// Request server to update account
			result = await this.accountStore.updateAccount(payload)
			.then(result => {
				this.account._etag = result._etag
				return result._status == 'OK'
			}).catch(err => {
				console.error("on update account", err)
				return false
			})

			if (result) {
				this.invalidateAccounts()
				this.executionResultMessage = `Користувача відредаговано.`
				this.executionOk = true

				this.goBackAfterTimeout()
			} else {
				this.executionResultMessage = 
					`Користувача не відредаговано: сервер повернув помилку.`
				this.executionOk = false
			}
		},

		async deleteUser(inGotify=true, inDB=true) {
			if (inGotify) {
				// Delete user in Gotify service
				await deleteUser(
					this.account[this.accountStore.schema.gotify.self][this.accountStore.schema.gotify.userId],
				).then((/*response*/) => {
					this.executionResultMessage = `Користувача для мобільного додатку видалено.`
					this.executionOk = true
				})
				.catch((err) => {
					console.error("on delete Gotify user, response:", err.response ? err.response.data : err)
					this.executionResultMessage = 
						`Користувача для мобільного додатку не видалено: сервер повернув помилку.`
					this.executionOk = false
				});
				if (!this.executionOk) {
					return
				}
			}

			if (inDB) {
				// Delete user from DB
				await this.accountStore.deleteAccount(
					this.account._id,
					this.account._etag,
				).then((/*result*/) => {
					//console.log("delete account result = ", result)
					this.executionResultMessage = `Користувача успішно видалено.`
					this.executionOk = true

					this.goBackAfterTimeout()
				}).catch(err => {
					console.error("on delete account", err)
					this.executionResultMessage = 
						`Користувача не видалено: сервер повернув помилку.`
					this.executionOk = false
				})
			}

			if (inGotify && inDB) {
				this.invalidateAccounts()
			}
		},

		goBackAfterTimeout() {
			setTimeout(
				() => {
					// step back
					this.$router.go(-1)
				},
				this.$options.waitAfterExecuteSuccessMs
			);
		},

		organization(id) {
			return this.organizations.filter((el) => el._id == id)[0]
		},

		onCheckDepartment(departmentId, isChecked, setFunc, curValue) {
			const newValue = [...curValue]
			
			if (isChecked && !curValue.includes(departmentId)) {
				newValue.push(departmentId)
			} else if (!isChecked && curValue.includes(departmentId)) {
				newValue.splice(newValue.indexOf(departmentId), 1)		
			}

			setFunc('departments_scope', newValue)
		},

		onCheckSector(sectorId, isChecked, setFunc, curValue) {
			const newValue = [...curValue]

			if (isChecked && !curValue.includes(sectorId)) {
				newValue.push(sectorId)
			} else if (!isChecked && curValue.includes(sectorId)) {
				newValue.splice(newValue.indexOf(sectorId), 1)
			}

			setFunc('sectors_scope', newValue)
		},
	},

	computed: {
		userId() {
			return this.$route.params.id
		},
		isCreateUser() { return this.userId == 'new' },
		isUpdateUser() { return this.userId != 'new' },

		isSelfUpdate() {
			if (!this.account) {
				return false
			} else {
				return this.account.login == this.accountStore.account[this.accountStore.schema.login]
			}
		},
	},
};
</script>




<style scoped>
.input-min-width {
	min-width: 64ch;
}

.accordion {
	--bs-accordion-active-bg: transparent;
}

.accordion-button:focus {
	box-shadow: none;
}

.accordion-button:not(.collapsed) {
	box-shadow: inset 0 0 0 var(--bs-accordion-border-color);
}
</style>
	